import type { LifeOsCollectionName, LifeOsState } from "../types";
import { cascadeDanglingRefs } from "./delete-cascade";
import { logError } from "./error-log";

const collectionNames: LifeOsCollectionName[] = [
  "tasks",
  "projects",
  "goals",
  "habits",
  "notes",
  "transactions",
  "budgets",
  "inbox",
  "events",
  "recurring",
  "accounts",
  "dailyPlans",
  "dailyReviews",
  "weeklyReviews",
  "templates",
  "currencyRates",
  "financeCategories",
];

export function stamp(state: LifeOsState): LifeOsState {
  return {
    ...state,
    updatedAt: new Date().toISOString(),
  };
}

export function markDirty(state: LifeOsState, collection: LifeOsCollectionName, id: string): LifeOsState {
  return {
    ...state,
    dirtyItems: [
      ...state.dirtyItems.filter((item) => !(item.collection === collection && item.id === id)),
      { collection, id, changedAt: new Date().toISOString() },
    ],
    deletedItems: state.deletedItems.filter((item) => !(item.collection === collection && item.id === id)),
  };
}

export function markDeleted(state: LifeOsState, collection: LifeOsCollectionName, id: string): LifeOsState {
  return {
    ...state,
    dirtyItems: state.dirtyItems.filter((item) => !(item.collection === collection && item.id === id)),
    deletedItems: [
      ...state.deletedItems.filter((item) => !(item.collection === collection && item.id === id)),
      { collection, id, deletedAt: new Date().toISOString() },
    ],
  };
}

export function hasPendingSyncItems(state: LifeOsState) {
  return state.dirtyItems.length > 0 || state.deletedItems.length > 0;
}

export function removeCollectionItem(state: LifeOsState, collection: LifeOsCollectionName, id: string): LifeOsState {
  if (!state[collection].some((item) => item.id === id)) {
    return state;
  }

  let next: LifeOsState = {
    ...markDeleted(state, collection, id),
    [collection]: state[collection].filter((item) => item.id !== id),
  };

  // Orphan checker: dọn mọi tham chiếu chết (goal/project/task/account) và
  // markDirty từng item bị sửa để thay đổi được đẩy lên cloud.
  const cascade = cascadeDanglingRefs(next, collection, id);
  next = cascade.state;
  for (const ref of cascade.dirtied) {
    next = markDirty(next, ref.collection, ref.id);
  }
  // Instance tương lai của series parent vừa xoá — xoá đệ quy (instance không
  // phải parent nên đệ quy dừng ngay sau 1 cấp; tombstone được ghi đầy đủ).
  for (const taskId of cascade.removeTaskIds) {
    next = removeCollectionItem(next, "tasks", taskId);
  }

  return next;
}

export function prepareReplacementState(current: LifeOsState, replacement: LifeOsState): LifeOsState {
  const now = new Date().toISOString();
  let next: LifeOsState = {
    ...replacement,
    dirtyItems: replacement.dirtyItems,
    deletedItems: replacement.deletedItems,
  };

  for (const collection of collectionNames) {
    const replacementIds = new Set(replacement[collection].map((item) => item.id));
    const missingCurrentIds = current[collection].filter((item) => !replacementIds.has(item.id)).map((item) => item.id);

    for (const item of replacement[collection]) {
      next = markDirty(next, collection, item.id);
    }

    for (const id of missingCurrentIds) {
      next = {
        ...next,
        deletedItems: [
          ...next.deletedItems.filter((item) => !(item.collection === collection && item.id === id)),
          { collection, id, deletedAt: now },
        ],
      };
    }
  }

  return {
    ...next,
    updatedAt: now,
  };
}

export function clearSyncedQueues(state: LifeOsState, syncedUpdatedAt: string): LifeOsState {
  if (state.updatedAt !== syncedUpdatedAt) {
    return state;
  }

  return {
    ...state,
    dirtyItems: [],
    deletedItems: [],
  };
}

function queueKey(item: { collection: LifeOsCollectionName; id: string }) {
  return `${item.collection}:${item.id}`;
}

export function clearSyncedQueueItems(state: LifeOsState, syncedState: LifeOsState): LifeOsState {
  if (state.updatedAt === syncedState.updatedAt) {
    return clearSyncedQueues(state, syncedState.updatedAt);
  }

  const syncedDirtyItems = new Map(syncedState.dirtyItems.map((item) => [queueKey(item), item.changedAt]));
  const syncedDeletedItems = new Map(syncedState.deletedItems.map((item) => [queueKey(item), item.deletedAt]));

  return {
    ...state,
    dirtyItems: state.dirtyItems.filter((item) => {
      const syncedChangedAt = syncedDirtyItems.get(queueKey(item));
      return !syncedChangedAt || new Date(item.changedAt).getTime() > new Date(syncedChangedAt).getTime();
    }),
    deletedItems: state.deletedItems.filter((item) => {
      const syncedDeletedAt = syncedDeletedItems.get(queueKey(item));
      return !syncedDeletedAt || new Date(item.deletedAt).getTime() > new Date(syncedDeletedAt).getTime();
    }),
  };
}

// Remote snapshots arrive id-ordered, while local mutations prepend new items.
// Sorting merged collections newest-first (createdAt desc, id tiebreak) matches the local
// prepend order, so lists no longer shuffle when a sync echo is adopted.
function compareNewestFirst(a: { id: string; createdAt?: string }, b: { id: string; createdAt?: string }): number {
  const aKey = a.createdAt ?? "";
  const bKey = b.createdAt ?? "";
  if (aKey !== bKey) {
    return bKey.localeCompare(aKey);
  }
  return a.id.localeCompare(b.id);
}

// LWW per-item (F1): remote chỉ thắng khi updated_at MỚI HƠN HẲN mốc queue local
// (>= giữ local — echo của chính push này quay về với updated_at === changedAt).
// Timestamp thiếu/hỏng → giữ local (an toàn, trùng hành vi trước LWW).
function remoteItemWins(remoteUpdatedAt: string | undefined, localQueuedAt: string): boolean {
  if (!remoteUpdatedAt) return false;
  const remoteTime = new Date(remoteUpdatedAt).getTime();
  const localTime = new Date(localQueuedAt).getTime();
  if (Number.isNaN(remoteTime) || Number.isNaN(localTime)) return false;
  return remoteTime > localTime;
}

/**
 * Merge remote snapshot với local, giữ pending queues.
 * Khi có remoteItemUpdatedAt (map "collection:id" → updated_at của từng row):
 * last-write-wins per item — pending local edit/delete CŨ HƠN bản remote bị
 * loại khỏi queue và nhận bản remote (thiết bị offline lâu không đè bản mới).
 * Không có map (caller cũ / test cũ) → local luôn thắng như trước.
 */
export function mergeRemoteWithPendingQueues(
  remote: LifeOsState,
  local: LifeOsState,
  remoteItemUpdatedAt?: Map<string, string>,
): LifeOsState {
  let dirtyItems = local.dirtyItems;
  let deletedItems = local.deletedItems;

  if (remoteItemUpdatedAt) {
    dirtyItems = local.dirtyItems.filter((item) => {
      const lost = remoteItemWins(remoteItemUpdatedAt.get(queueKey(item)), item.changedAt);
      if (lost) logError("sync", `LWW: bỏ sửa local cũ hơn bản cloud (${queueKey(item)})`);
      return !lost;
    });
    // Tombstone vs remote mới hơn: item được sửa trên thiết bị khác SAU khi
    // xoá local → huỷ tombstone, giữ bản remote (không xoá dữ liệu mới hơn).
    deletedItems = local.deletedItems.filter((item) => {
      const lost = remoteItemWins(remoteItemUpdatedAt.get(queueKey(item)), item.deletedAt);
      if (lost) logError("sync", `LWW: huỷ xoá local — cloud sửa mới hơn (${queueKey(item)})`);
      return !lost;
    });
  }

  const merged = {
    ...remote,
    dirtyItems,
    deletedItems,
  };

  for (const collection of collectionNames) {
    const dirtyIds = new Set(dirtyItems.filter((item) => item.collection === collection).map((item) => item.id));
    const deletedIds = new Set(deletedItems.filter((item) => item.collection === collection).map((item) => item.id));
    const remoteItems = merged[collection].filter((item) => !deletedIds.has(item.id));
    const localDirtyItems = local[collection].filter((item) => dirtyIds.has(item.id) && !deletedIds.has(item.id));
    const localDirtyById = new Map(localDirtyItems.map((item) => [item.id, item]));
    const mergedItems = remoteItems.map((item) => localDirtyById.get(item.id) ?? item);
    const remoteIds = new Set(remoteItems.map((item) => item.id));

    merged[collection] = [
      ...localDirtyItems.filter((item) => !remoteIds.has(item.id)),
      ...mergedItems,
    ].sort(compareNewestFirst) as never;
  }

  return merged;
}
